import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Stack,
  FormControl,
  IconButton,
  CircularProgress
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { VariableSizeList as List } from 'react-window';
import { filterUniqueOptions } from '../../utils/Utils';

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
      padding: '8px',
      borderBottom: '1px solid #DFDFDF'
    }
  });
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const {
    children,
    selectedValues,
    memorizedOptions,
    isLastItemUnChecked,
    unCheckedValue,
    ...other
  } = props;
  const itemData = useMemo(() => React.Children.toArray(children), [children]);
  const itemCount = itemData.length;
  const itemSize = 36;
  const listRef = useRef(null); // Ref to access the list
  const isSelectedAll = selectedValues.length === memorizedOptions.length;
  let latestSelection;
  // if want to find the
  if (isLastItemUnChecked === undefined) {
    latestSelection = unCheckedValue?.[0];
  } else {
    latestSelection = !isLastItemUnChecked ? unCheckedValue : selectedValues.at(-1);
  }
  const latestSelectionIndex = useMemo(
    () => memorizedOptions.findIndex((option) => option === latestSelection),
    [latestSelection, memorizedOptions]
  );

  useEffect(() => {
    if (isSelectedAll || selectedValues?.length === 0) {
      requestAnimationFrame(() => {
        listRef?.current?.scrollToItem(0, 'smart');
      });
    } else {
      requestAnimationFrame(() => {
        listRef?.current?.scrollToItem(latestSelectionIndex, 'smart');
      });
    }
  }, [latestSelection, memorizedOptions, selectedValues, isSelectedAll, isLastItemUnChecked]);

  return (
    <div ref={ref} {...other}>
      <List
        ref={listRef}
        height={Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING}
        itemCount={itemCount}
        itemSize={() => itemSize}
        width="100%">
        {(props) => renderRow({ ...props, data: itemData })}
      </List>
    </div>
  );
});

const CustomAutoComplete = ({
  placeholder = 'Filter',
  label = '',
  size = 'small',
  options = [],
  selectedOptions = [],
  onOptionsChange
}) => {
  const [open, setOpen] = useState(false);
  const isLoading = false;
  const [inputValue, setInputValue] = useState('');
  const memorizedOptions = useMemo(() => filterUniqueOptions(options), [options]);
  const [lastItemCheckedState, setCheckedState] = useState(false);
  const [unCheckedValue, setUnCheckedValue] = useState('');

  const handleChange = (event, value) => {
    let unCheckedValueName = '';
    if (event.target.checked === undefined) {
      if (selectedOptions?.length <= value?.length) {
        unCheckedValueName = value
          ?.filter((list) => (selectedOptions?.includes(list) ? null : list))
          ?.filter((li) => li !== null);
        setUnCheckedValue(unCheckedValueName);
      } else {
        unCheckedValueName = selectedOptions
          ?.filter((list) => (value?.includes(list) ? null : list))
          ?.filter((li) => li !== null);
        setUnCheckedValue(unCheckedValueName);
      }
    } else if (!event.target.checked) {
      unCheckedValueName = selectedOptions
        ?.filter((item) => (!value?.includes(item) ? item : null))
        ?.filter((li) => li !== null)?.[0];
      setUnCheckedValue(unCheckedValueName);
    }
    setCheckedState(event.target.checked);
    if (value.find((option) => option === 'Select All')) {
      if (selectedOptions.length === memorizedOptions.length) {
        onOptionsChange([]);
      } else {
        onOptionsChange(memorizedOptions);
      }
    } else {
      onOptionsChange(value);
    }
  };

  const handleClear = () => {
    setOpen(true);
    setInputValue('');
    onOptionsChange([]);
  };

  const handleInputChange = (e, val, reason) => {
    if (reason === 'input') {
      setInputValue(val);
    }
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  return (
    <FormControl sx={{ width: '100%' }}>
      {label && (
        <Typography htmlFor="my-input" variant="extraSmall" sx={{ color: '#616161', mb: '8px' }}>
          {label}
        </Typography>
      )}
      <Autocomplete
        multiple
        id="my-input"
        data-testid="auto-complete"
        options={['Select All', ...memorizedOptions]}
        value={selectedOptions}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        disableCloseOnSelect
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        ListboxComponent={(props) => (
          <ListboxComponent
            {...props}
            selectedValues={selectedOptions}
            memorizedOptions={memorizedOptions}
            isLastItemUnChecked={lastItemCheckedState}
            unCheckedValue={unCheckedValue}
          />
        )}
        renderOption={(props, option, { selected }, index) => {
          return (
            <li
              {...props}
              key={`${option}-${index}`}
              style={{
                padding: '8px',
                borderBottom: '1px solid #DFDFDF',
                color: '#616161'
              }}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected || selectedOptions.length === memorizedOptions.length}
                sx={{ padding: 'initial' }}
              />
              <Typography
                title={option}
                variant="small"
                sx={{
                  whiteSpace: 'nowrap',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: '#616161'
                }}>
                {option}
              </Typography>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0px 4px 0px 0px !important',
                '& .MuiAutocomplete-input': {
                  padding: '4px 8px !important',
                  fontSize: size === 'small' ? '12px' : '14px',
                  lineHeight: size === 'small' ? '18px' : '32px',
                  minWidth: '0px',
                  height: size === 'small' ? '17px' : '32px'
                }
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {selectedOptions.length > 0 && (
                    <Stack
                      sx={{
                        backgroundColor: '#AAD2E2',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        flexDirection: 'row',
                        gap: '2px',
                        alignItems: 'center'
                      }}>
                      <Typography
                        sx={{
                          fontSize: '10px',
                          lineHeight: '14px',
                          color: '#000000'
                        }}>{`+${selectedOptions.length}`}</Typography>
                      <IconButton
                        aria-label="Clear filter"
                        size="small"
                        sx={{ padding: 0 }}
                        onClick={handleClear}>
                        <CloseIcon
                          sx={{
                            color: '#000000',
                            width: '14px',
                            height: '14px'
                          }}
                        />
                      </IconButton>
                    </Stack>
                  )}
                </>
              ),
              startAdornment: null
            }}
            value=""
          />
        )}
        ListboxProps={{
          sx: { maxHeight: '100%', position: 'relative' }
        }}
        PaperComponent={({ children }) => (
          <Box
            sx={{
              border: '1px solid #DFDFDF',
              backgroundColor: '#FBFCFE',
              boxShadow: '0px 4px 24px 0px #3A3A3A14',
              borderRadius: '4px',
              padding: '8px',
              minWidth: '200px',
              width: '100%'
            }}>
            {isLoading && (
              <Box
                sx={{
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <CircularProgress sx={{ color: '#005CBC', fontSize: '36px' }} />
              </Box>
            )}
            {!isLoading && memorizedOptions.length === 0 && (
              <Typography
                variant="small"
                sx={{
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                No Options
              </Typography>
            )}
            {!isLoading && memorizedOptions.length > 0 && children}
          </Box>
        )}
      />
    </FormControl>
  );
};

export default CustomAutoComplete;
